import { createPdfHelpers, uploadAndRegisterPdf } from './gestion-tecnica-documentos'
import { RebaAssessmentRow } from './gestion-tecnica-reba'
import { calcularPuntuacionGrupoA, calcularPuntuacionGrupoB } from './reba'

// Genera el reporte técnico PDF de una evaluación REBA, siguiendo el mismo
// patrón que generateRulaReportPDF(): createPdfHelpers() para el flujo de
// texto + uploadAndRegisterPdf() para subir el blob y registrarlo en
// sso_documents.
//
// checklist_item_id: 'GT-06' — mismo ítem que RULA (confirmado en
// compliance-engine.ts: "Informe de evaluación de riesgos ergonómicos y
// psicosociales", Decisión 584 Art. 11, DE 255 (2024) Art. 44/45/46, AM 196
// (2024)) — el ítem es genérico, sin atadura a una metodología específica,
// así que RULA y REBA comparten el mismo código de checklist.
export async function generateRebaReportPDF(
  evaluacion: RebaAssessmentRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { txt, skip, hline, doc, drawTable } = helpers

  const { inputs, resultado } = evaluacion

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Evaluación Ergonómica — Método REBA', 12, 'bold', 'center', 0, 6)
  txt(`${evaluacion.descripcion} — Lado ${evaluacion.lado === 'izquierdo' ? 'Izquierdo' : 'Derecho'}`, 11, 'bold', 'center', 0, 6)
  hline()

  // b. Datos de entrada
  txt('Datos de entrada', 11, 'bold', 'left', 0, 4)
  txt('Fuente metodológica: REBA (Rapid Entire Body Assessment — Hignett & McAtamney, 2000)', 9, 'italic', 'left', 0, 3)

  txt('Grupo A — Tronco, cuello y piernas', 10, 'bold', 'left', 0, 3)
  txt(`Tronco: ${inputs.rangoTronco} — inclinación lateral o rotación: ${inputs.modTronco.inclinacionLateralORotacion ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Cuello: ${inputs.rangoCuello} — inclinación lateral o rotación: ${inputs.modCuello.inclinacionLateralORotacion ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Piernas: ${inputs.condicionPiernas} — flexión de rodillas: ${inputs.modPiernas.flexionRodillas ?? 'ninguna'}`, 9, 'normal', 'left', 4, 4)

  txt('Grupo B — Miembros superiores', 10, 'bold', 'left', 0, 3)
  txt(`Brazo: ${inputs.rangoBrazo} — abducido/rotado: ${inputs.modBrazo.abducidoORotado ? 'sí' : 'no'}, hombro elevado: ${inputs.modBrazo.hombroElevado ? 'sí' : 'no'}, punto de apoyo: ${inputs.modBrazo.puntoApoyoOFavorGravedad ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Antebrazo: ${inputs.rangoAntebrazo}`, 9, 'normal', 'left', 4, 2)
  txt(`Muñeca: ${inputs.rangoMuneca} — torsión o desviación: ${inputs.modMuneca.torsionODesviacion ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 4)

  txt('Carga/fuerza y agarre', 10, 'bold', 'left', 0, 3)
  txt(`Carga/fuerza: ${inputs.cargaFuerza} — aplicada bruscamente: ${inputs.fuerzaBrusca ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Calidad del agarre: ${inputs.calidadAgarre}`, 9, 'normal', 'left', 4, 4)

  txt('Actividad muscular', 10, 'bold', 'left', 0, 3)
  txt(`Postura estática >1 min: ${inputs.tipoActividad.posturaEstaticaMasDe1Min ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Movimientos repetitivos >4/min: ${inputs.tipoActividad.movimientosRepetitivosMasDe4PorMin ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 2)
  txt(`Cambios de postura importantes o inestable: ${inputs.tipoActividad.cambiosPosturaImportantesOInestable ? 'sí' : 'no'}`, 9, 'normal', 'left', 4, 4)
  hline()

  // c. Resultado
  txt('Resultado del cálculo', 11, 'bold', 'left', 0, 4)
  txt(`Puntuación Grupo A: ${resultado.grupoA} (tronco=${resultado.puntuacionTronco}, cuello=${resultado.puntuacionCuello}, piernas=${resultado.puntuacionPiernas})`, 9, 'normal', 'left', 0, 2)
  txt(`Puntuación Grupo B: ${resultado.grupoB} (brazo=${resultado.puntuacionBrazo}, antebrazo=${resultado.puntuacionAntebrazo}, muñeca=${resultado.puntuacionMuneca})`, 9, 'normal', 'left', 0, 2)

  skip(3)

  // Tabla Grupo A: 3 sub-tablas por Cuello (1-3), cada una 5 filas (Tronco 1-5) x 4 columnas (Piernas 1-4)
  for (let cuelloIdx = 1; cuelloIdx <= 3; cuelloIdx++) {
    const colHeaders = [1, 2, 3, 4].map(p => `Piernas ${p}`);
    const rowHeaders = [1, 2, 3, 4, 5].map(t => `Tronco ${t}`);
    const data = [1, 2, 3, 4, 5].map(tronco => {
      const row = [];
      for (let p = 1; p <= 4; p++) {
        row.push(calcularPuntuacionGrupoA(tronco, cuelloIdx, p));
      }
      return row;
    });
    drawTable({
      title: `Tabla Grupo A — Cuello ${cuelloIdx}`,
      colHeaders,
      rowHeaders,
      data,
      highlightRow: resultado.puntuacionCuello === cuelloIdx ? (resultado.puntuacionTronco - 1) : -1,
      highlightCol: resultado.puntuacionCuello === cuelloIdx ? (resultado.puntuacionPiernas - 1) : -1,
    });
  }

  skip(3)

  // Tabla Grupo B: 2 sub-tablas por Antebrazo (1-2), cada una 6 filas (Brazo 1-6) x 3 columnas (Muñeca 1-3)
  for (let antebrazoIdx = 1; antebrazoIdx <= 2; antebrazoIdx++) {
    const colHeadersB = [1, 2, 3].map(m => `Muñeca ${m}`);
    const rowHeadersB = [1, 2, 3, 4, 5, 6].map(b => `Brazo ${b}`);
    const dataB = [1, 2, 3, 4, 5, 6].map(brazo => {
      const row = [];
      for (let m = 1; m <= 3; m++) {
        row.push(calcularPuntuacionGrupoB(brazo, antebrazoIdx, m));
      }
      return row;
    });
    drawTable({
      title: `Tabla Grupo B — Antebrazo ${antebrazoIdx}`,
      colHeaders: colHeadersB,
      rowHeaders: rowHeadersB,
      data: dataB,
      highlightRow: resultado.puntuacionAntebrazo === antebrazoIdx ? (resultado.puntuacionBrazo - 1) : -1,
      highlightCol: resultado.puntuacionAntebrazo === antebrazoIdx ? (resultado.puntuacionMuneca - 1) : -1,
    });
  }

  txt(`Puntuación A: ${resultado.puntuacionA} (Grupo A + carga/fuerza)   Puntuación B: ${resultado.puntuacionB} (Grupo B + agarre)`, 9, 'normal', 'left', 0, 2)
  txt(`Puntuación C: ${resultado.puntuacionC}`, 9, 'normal', 'left', 0, 2)
  txt(`Puntuación Final REBA: ${resultado.puntuacionFinal}`, 11, 'bold', 'left', 0, 3)
  txt(
    `Nivel ${resultado.nivelActuacion.nivel} — Riesgo ${resultado.nivelActuacion.riesgo}: ${resultado.nivelActuacion.actuacion}`,
    10, 'bold', 'left', 0, 4
  )
  hline()

  // d. Disclaimer — mismo criterio que RULA/Ruido: el PDF documenta
  // exactamente lo que ve el usuario en pantalla, no una versión distinta.
  txt(
    'Tablas de doble/triple entrada (Grupo A, Grupo B, Puntuación C) extraídas de ' +
    'Hignett, S. & McAtamney, L. (2000). Rapid entire body assessment (REBA). Applied Ergonomics, 31(2), 201-205, y verificadas celda por celda contra la fuente. ' +
    'Este reporte no reemplaza el juicio de un profesional de ergonomía para el rediseño de la tarea.',
    8, 'italic', 'left', 0, 4
  )

  const blob = doc.output('blob')
  const filename = `REBA_${evaluacion.descripcion.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${evaluacion.id}-reba`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte REBA - ${evaluacion.descripcion}`,
    description: 'Generado automáticamente desde Metodología REBA',
    checklist_item_id: 'GT-06',
    uploaded_by: uploadedBy,
  })
}
